import React, { forwardRef, useCallback, useEffect } from 'react';
import { useOverflowStatus } from '@atlaskit/atlassian-navigation';
import { useFavoritePlans as useFavorite } from '@atlassian/jira-favorites-store/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { ADVANCED_ROADMAPS_TASK } from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import { useShouldShowNudge } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { usePlansItems } from '@atlassian/jira-plans-item-store/src/index.tsx';
import { useNavigationPlansVisibility } from '@atlassian/jira-plans-item-visibility/src/index.tsx';
import { NAVIGATION_ITEM_ID, DEFAULT_LOAD_COUNT_FAV } from '../../common/constants.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import PopupBoxWrapper from '../../common/ui/popup-box-wrapper/index.tsx';
import { Popup } from '../../common/ui/popup/main.tsx';
import { useIsStandardOrFreeJSW } from '../../controllers/edition/index.tsx';
import { MenuTrigger } from './menu-trigger/index.tsx';
import Menu from './menu/main.tsx';
import messages from './messages.tsx';

const PlansDropdown = () => {
	const [isPlansVisible] = useNavigationPlansVisibility();
	const [{ hasFetchedOnce: hasFetchedOncePlans }, { loadFavoritePlans: loadFavorite }] =
		useFavorite();
	const [{ hasSuccessOnce }, { load }] = usePlansItems();
	const { formatMessage } = useIntl();
	const isStandardOrFree = useIsStandardOrFreeJSW();

	// Initialise items data
	useEffect(() => {
		!hasSuccessOnce && !isStandardOrFree && isPlansVisible && load();
	}, [hasSuccessOnce, load, isPlansVisible, isStandardOrFree]);

	// Keep items data fresh
	const onBeforeMenuOpen = useCallback(
		() => {
			if (!isStandardOrFree) {
				load();
				if (!hasFetchedOncePlans) loadFavorite(DEFAULT_LOAD_COUNT_FAV);
			}
		},
		// We dont care about hasFetchedOncePlans, it will generate a new call back which then closes the popup
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[load, loadFavorite],
	);

	if (!isPlansVisible) {
		return null;
	}

	/**
	 * Cannot do <Popup ... MenuTrigger={MenuTrigger} /> because
	 * Popup passes exact common MenuTrigger props which doesn't have DI components
	 * that makes Flow complains about props passing to customized MenuTrigger
	 * */

	// @ts-expect-error - TS2322 - Type '((instance: unknown) => void) | MutableRefObject<unknown> | null' is not assignable to type 'Ref<MenuTriggerProps> | undefined'.
	// FIXME: Navigation should fix that by extracting the forwarded component
	// eslint-disable-next-line react/no-unstable-nested-components
	const FinalMenuTrigger = forwardRef((props, ref) => <MenuTrigger {...props} ref={ref} />);

	return (
		<PopupBoxWrapper>
			<Popup
				menuId={MENU_ID.PLANS}
				navigationItemId={NAVIGATION_ITEM_ID.PLANS}
				Menu={Menu}
				// @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<RefAttributes<unknown>>' is not assignable to type 'ComponentType<MenuTriggerProps> | undefined'.
				MenuTrigger={FinalMenuTrigger}
				onBeforeMenuOpen={onBeforeMenuOpen}
				label={formatMessage(messages.plans)}
			>
				{formatMessage(messages.plans)}
			</Popup>
		</PopupBoxWrapper>
	);
};

export const Plans = () => (
	<TopLevelErrorBoundary id={MENU_ID.PLANS}>
		<PlansDropdown />
	</TopLevelErrorBoundary>
);

export const OpenOverflowMenuForPlansSpotlightObserver = () => {
	const [isPlansVisible] = useNavigationPlansVisibility();
	const [shouldShow] = useShouldShowNudge({
		itemNames: [ADVANCED_ROADMAPS_TASK],
	});
	const WRAPPER_UID = 'plans-spotlight-target-wrapper';
	const spotlightVisible =
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		!__SERVER__ && document.querySelector(`[data-uid="${WRAPPER_UID}"]`) != null;

	const { openOverflowMenu } = useOverflowStatus();

	useEffect(() => {
		isPlansVisible && shouldShow && !spotlightVisible && openOverflowMenu();
	}, [isPlansVisible, openOverflowMenu, shouldShow, spotlightVisible]);

	return null;
};
